/** @format */

import {MultilineChartInterface} from 'components/charts/multiline-chart/model/common';

import {generateGraphPointName} from 'helpers/miscHelpers';

import {TooltipRow, TooltipValue} from 'components/charts/multiline-chart/model/tooltip';

import {Discreteness} from 'components/PeriodSelector/model';

import {AnalyticGoalFromQuery, DcSettingsFromQuery} from '../../models/api';
import {ChartIds} from '../../models/chartIds';
import {SavedDashboardsList, SavedMenuCRWidgetSettings} from '../../models/savedSpaceSettings';
import {Space, SpaceId} from '../../models/spaces';
import {WidgetBlock} from '../../models/widgets';

import {getTooltipDelta} from '../../mapping/tooltip/getTooltipDelta';

export const getSavedCrMap = (
  spaceId: SpaceId,
  digitalCastSettings: DcSettingsFromQuery,
): Record<string, Pick<SavedMenuCRWidgetSettings, 'aGoalId' | 'bGoalId'>> => {
  try {
    const raw =
      typeof digitalCastSettings === 'string'
        ? digitalCastSettings
        : JSON.stringify(digitalCastSettings);

    const saved: SavedDashboardsList | null = raw ? JSON.parse(raw) : null;
    const cr = saved?.[spaceId]?.menuWidgetSettings?.cr || [];

    return cr.reduce<Record<string, Pick<SavedMenuCRWidgetSettings, 'aGoalId' | 'bGoalId'>>>(
      (acc, item) => {
        acc[item.id] = {aGoalId: item.aGoalId, bGoalId: item.bGoalId};
        return acc;
      },
      {},
    );
  } catch {
    return {};
  }
};

export const ensureCrSettings = (
  space: Space,
  savedCr: Record<string, Pick<SavedMenuCRWidgetSettings, 'aGoalId' | 'bGoalId'>>,
  analyticGoals?: ReadonlyArray<AnalyticGoalFromQuery>,
): Space => {
  const settings = {...space.widgetsSettings};
  const basics = new Set(['clicks', 'impressions', 'shows']);
  const validGoalIds = new Set((analyticGoals || []).map(g => String(g.id)));

  const toStringSafe = (v?: string | number) => (v === undefined ? '' : String(v));
  const isBasic = (id: string) => basics.has(id);
  const isValidGoal = (id: string) => id !== '' && (isBasic(id) || validGoalIds.has(id));
  const isCrId = (id: ChartIds) => id.toString().startsWith('funnel_cr_');

  const selectedIds = new Set<ChartIds>([
    ...space.widgetsBoardSelected,
    ...space.widgetsMenuSelected,
  ]);

  const crIds = Array.from(selectedIds).filter(isCrId);

  const idsToRemove = new Set<ChartIds>();

  crIds.forEach(id => {
    const saved = savedCr[id];
    const existing = settings[id]?.custom as
      | {aGoalId?: string | number; bGoalId?: string | number}
      | undefined;
    const aId = toStringSafe(saved?.aGoalId ?? existing?.aGoalId);
    const bId = toStringSafe(saved?.bGoalId ?? existing?.bGoalId);

    if (!isValidGoal(aId) || !isValidGoal(bId)) {
      idsToRemove.add(id);
      delete settings[id];
      return;
    }

    if (!settings[id]) {
      settings[id] = {custom: {aGoalId: aId, bGoalId: bId}};
      return;
    }

    if (!settings[id].custom) {
      settings[id] = {...settings[id], custom: {aGoalId: aId, bGoalId: bId}};
    }
  });

  const filterOutRemoved = (arr: ChartIds[]) => arr.filter(v => !idsToRemove.has(v));

  return {
    ...space,
    widgetsSettings: settings,
    widgetsBoardSelected: filterOutRemoved(space.widgetsBoardSelected),
    widgetsMenuSelected: filterOutRemoved(space.widgetsMenuSelected),
    chartSelected: {
      current: filterOutRemoved(space.chartSelected.current),
      previous: filterOutRemoved(space.chartSelected.previous),
    },
  };
};

interface Options {
  base: MultilineChartInterface<ChartIds>;
  analyticGoals: ReadonlyArray<AnalyticGoalFromQuery>;
  rechartsDates: string[];
  discreteness: Discreteness;
}

export const buildCrChartsForSpace = ({
  base,
  rechartsDates,
  analyticGoals,
  discreteness,
}: Options): MultilineChartInterface<ChartIds> => {
  const funnelChartIds = (analyticGoals || []).map(g => g.name as ChartIds);

  const accumulatedPoints = rechartsDates.reduce<{name: string; date: string}[]>(
    (acc, cur, index) => {
      const point = generateGraphPointName(index);
      return [
        ...acc,
        {
          name: point,
          date: cur,
        },
      ];
    },
    [],
  );

  const orderedPointNames = accumulatedPoints.map(d => d.name);

  const updatedTooltipDataList = {...base.tooltipDataList};

  for (let i = 0; i < funnelChartIds.length; i++) {
    const goalChartId = funnelChartIds[i];
    const chart = base.charts.find(c => c.chartId === goalChartId);

    if (!chart) {
      continue;
    }

    for (let j = 0; j < orderedPointNames.length; j++) {
      const pointName = orderedPointNames[j];
      const tooltipData = updatedTooltipDataList[pointName];

      if (!tooltipData) {
        continue;
      }

      const existingRows = tooltipData.rows || [];
      const rowsWithoutGoal = existingRows.filter(row => row.chartId !== goalChartId);
      const point = chart.chartsData.find(d => d.name === pointName);

      if (!point) {
        continue;
      }

      const value: TooltipValue = {
        primary: String(point?.primary ?? 0),
      };

      if (point?.secondary !== undefined) {
        value.secondary = String(point.secondary);
      }

      const newRow: TooltipRow<ChartIds> = {
        chartId: goalChartId,
        label: chart.label,
        value,
      };

      const isNeedDelta = (discreteness === 'week' || discreteness === 'month') && j > 0;
      const prevPointName = isNeedDelta ? orderedPointNames[j - 1] : undefined;
      const prevPoint = chart.chartsData.find(d => d.name === prevPointName);

      const delta = getTooltipDelta({
        current: point,
        previous: prevPoint,
        roundNumberBy: 0,
      });

      if (delta) {
        newRow.delta = delta;
      }

      updatedTooltipDataList[pointName] = {
        ...tooltipData,
        rows: [...rowsWithoutGoal, newRow],
      };
    }
  }

  return {
    ...base,
    tooltipDataList: updatedTooltipDataList,
  };
};

export const addCrIdsToBlocks = (baseBlocks: WidgetBlock[], space: Space) => {
  // CR могут быть только что добавлены и присутствовать в selections, но ещё не иметь widgetsSettings
  const selectedIds = new Set<ChartIds>([
    ...space.widgetsBoardSelected,
    ...space.widgetsMenuSelected,
  ]);
  const crIds = Array.from(selectedIds).filter((id): id is ChartIds =>
    String(id).startsWith('funnel_cr_'),
  );

  if (!crIds.length) return baseBlocks;

  const cloned = baseBlocks.map(b => ({...b}));
  const idx = cloned.findIndex(b => b.id === 'funnel');

  if (idx === -1) return baseBlocks;

  const funnel = {...cloned[idx]};
  const subMenu = funnel.subMenu
    ? {...funnel.subMenu, widgets: [...funnel.subMenu.widgets]}
    : {addButtonText: '% Добавить CR', widgets: []};

  crIds.forEach(id => {
    if (!subMenu.widgets.includes(id)) subMenu.widgets.push(id);
  });

  funnel.subMenu = subMenu;
  cloned[idx] = funnel;

  return cloned;
};
